"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import log from "logbeacon";
import { safeStringifyError } from "utils";
import { openOnboardingPaywall } from "@/src/components/ChatOnboarding/openOnboardingPaywall";
import {
  showBriefCreateNoteFailedToast,
  showBriefTrackerCreatedToast,
} from "@/src/screens/brief/utils/briefTrackerCreatedToast";
import { buildTrackerDetailPath } from "@/src/utils/trackerRoute";
import { trackEvent } from "@/src/utils/tracking";
import { QUERY_KEYS } from "@/src/config/constant";
import { useIsPc, useShellRouter } from "@/src/hooks";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { useActivateTracker } from "./useActivateTracker";

export type TrackerActivateEntry = "chat_start_tracking";

export type TrackerActivateOutcome =
  | { outcome: "activated"; trackerId: string }
  | { outcome: "dismissed" }
  | { outcome: "error"; error: unknown };

export type RunTrackerActivateParams = {
  trackerId: string;
  entry: TrackerActivateEntry;
};

/**
 * Chat start_tracking Paywall gate + activate API。
 * @see fe-spec-edg-118 §3.5
 */
export function useTrackerActivatePaywallGate() {
  const [isPC] = useIsPc();
  const router = useShellRouter();
  const queryClient = useQueryClient();
  const { data: memberPlanInfo } = useMemberUser();
  const isMember = memberPlanInfo?.isMember === true;
  const { mutateAsync: activateTracker, isPending: isActivating } =
    useActivateTracker();

  const openTrackerPaywall = useCallback(async () => {
    trackEvent("paywall_view", { paywall_source: "tracker" });
    return openOnboardingPaywall(isPC, { analyticsSource: "tracker" });
  }, [isPC]);

  const requirePaywallIfNonMember = useCallback(async (): Promise<boolean> => {
    if (isMember) {
      return true;
    }

    const paywallOutcome = await openTrackerPaywall();
    if (paywallOutcome !== "purchased") {
      return false;
    }

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
    return true;
  }, [isMember, openTrackerPaywall, queryClient]);

  const runActivate = useCallback(
    async (
      params: RunTrackerActivateParams,
    ): Promise<TrackerActivateOutcome> => {
      const trackerId = params.trackerId.trim();
      if (!trackerId) {
        return { outcome: "error", error: new Error("trackerId is required") };
      }

      trackEvent("tracker_start_tap", {
        is_paid: isMember,
        entry_source: "chat",
        tracker_id: trackerId,
      });

      try {
        const paid = await requirePaywallIfNonMember();
        if (!paid) {
          return { outcome: "dismissed" };
        }

        const result = await activateTracker(trackerId);
        trackEvent("tracker_created_success", {
          tracker_id: result.trackerId,
          entry_source: "chat",
        });
        showBriefTrackerCreatedToast(() => {
          router.push(buildTrackerDetailPath(result.trackerId));
        });
        return { outcome: "activated", trackerId: result.trackerId };
      } catch (error) {
        log.error(
          "[TrackerActivateGate] activate failed",
          safeStringifyError(error),
        );
        showBriefCreateNoteFailedToast();
        return { outcome: "error", error };
      }
    },
    [activateTracker, isMember, requirePaywallIfNonMember, router],
  );

  return {
    runActivate,
    isActivating,
    isMember,
  };
}
